import React, { useState, useEffect, useCallback } from 'react';

import { GridComponent, ColumnsDirective, ColumnDirective, Page, Toolbar, Sort, Filter, Inject, Edit, CommandColumn, ForeignKey } from '@syncfusion/ej2-react-grids';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { type MaterialDto } from '../api/models/MaterialDto';
import { type OperationInputQuantityDto } from '../api/models/OperationInputQuantityDto';
import { MaterialControllerService } from '../api/services/MaterialControllerService';
import { useTranslation } from 'react-i18next';
import { useDropdownFormInitializer } from '../hooks/useDropdownFormInitializer';
// Custom form dialog for input quantities

interface InputQuantitiesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (inputQuantities: OperationInputQuantityDto[]) => void;
  initialQuantities: OperationInputQuantityDto[];
  operationName?: string;
}

const InputQuantitiesDialog: React.FC<InputQuantitiesDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  initialQuantities,
  operationName
}) => {
  const { t } = useTranslation();
  const [materials, setMaterials] = useState<MaterialDto[]>([]);
  const [inputQuantities, setInputQuantities] = useState<(OperationInputQuantityDto & { rowId: string })[]>([]);
  const [loading, setLoading] = useState(true); // Start with loading true
  const gridRef = React.useRef<any>(null);

  // Load data function for the hook
  const loadData = async () => {
    const materialsData = await MaterialControllerService.findAll();

    // Input materials: intermediate products (INTERMEDIATE_PRODUCT) and raw materials (RAW_MATERIAL) only.
    // Finished products cannot be the input of another production step.
    const eligible = materialsData.filter(material =>
      material.materialType === 'INTERMEDIATE_PRODUCT' || material.materialType === 'RAW_MATERIAL'
    );

    // Ensure all materials have proper uuid and name fields
    const validMaterials = eligible.filter(material => material.uuid && material.name);

    return { materials: validMaterials };
  };

  // Set state functions for the hook
  const setStateFunctions = {
    materials: setMaterials
  };

  // Use the custom hook for proper timing
  useDropdownFormInitializer(
    isOpen,
    loadData,
    setStateFunctions,
    () => {
      setLoading(false);
    },
    {}
  );

  // Initialize input quantities when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Add rowId to each quantity if it doesn't have one
      const quantitiesWithRowIds = initialQuantities.map(qty => ({
        ...qty,
        rowId: (qty as any).rowId || `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      }));
      setInputQuantities(quantitiesWithRowIds);
    }
  }, [isOpen, initialQuantities]);

  const dialogContent = useCallback(() => {
    // Find material by ID for display - handle string IDs (UUIDs)
    const findMaterialById = (materialId: string | undefined) => {
      if (!materialId) {
        return null;
      }

      return materials.find(material => material.uuid === materialId);
    };

    // Translate unit type to display text
    const translateUnitType = (unitType: string | undefined) => {
      if (!unitType) return t('routing.form.unit');

      const unitTypeMap: Record<string, string> = {
        'PIECE': t('form.unitType.piece'),
        'MASS': t('form.unitType.mass'),
        'VOLUME': t('form.unitType.volume'),
        'LENGTH': t('form.unitType.length'),
        'AREA': t('form.unitType.area')
      };

      return unitTypeMap[unitType] || unitType;
    };

    const handleSave = () => {
      // Remove rowId from quantities before saving
      const quantitiesToSave = inputQuantities.map(({ rowId, ...qty }) => qty);
      onSave(quantitiesToSave);
      // Don't call onClose immediately - let the parent handle closing
      // onClose();
    };

    return (
      <div className="p-4">
        <div className="space-y-4">
          {/* Input Quantities Grid */}
          <div>
            <div className="mb-2">
              <h5 className="text-md font-semibold">
                {t('routing.form.inputQuantities')} ({inputQuantities.length})
              </h5>
            </div>
            
            <div className="h-64">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <div className="text-md font-medium">{t('common.loading', 'Loading...')}</div>
                  </div>
                </div>
              ) : (
                <>
                  <GridComponent
                    ref={gridRef}
                    key={`grid-${isOpen ? 'open' : 'closed'}`}
                    dataSource={inputQuantities}
                    height="100%"
                    allowSorting={true}
                    allowFiltering={false}
                    locale="de-DE"
                    toolbar={[
                      { text: t('actions.add'), tooltipText: t('actions.add'), prefixIcon: 'e-add', id: 'add' },
                      { text: t('actions.delete'), tooltipText: t('actions.delete'), prefixIcon: 'e-delete', id: 'delete' }
                    ]}
                    toolbarClick={(args) => {
                      if (!args.item) return;
                      
                      switch (args.item.text) {
                        case t('actions.add'):
                          // Create new quantity and add to state
                          const newQuantity: OperationInputQuantityDto & { rowId: string } = {
                            materialId: '', // Use empty string instead of undefined
                            quantity: 1,
                            unit: '',
                            rowId: `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                          };
                          setInputQuantities(prev => [...prev, newQuantity]);
                          
                          // Start editing the new row after a short delay to ensure it's added
                          setTimeout(() => {
                            if (gridRef.current) {
                              const lastIndex = inputQuantities.length; // New row will be at this index
                              gridRef.current.selectRow(lastIndex);
                              gridRef.current.startEdit(lastIndex, 'materialId');
                            }
                          }, 100);
                          break;
                        case t('actions.delete'):
                          // Handle deletion
                          if (gridRef.current) {
                            const selectedRowIndexes = gridRef.current.getSelectedRowIndexes();
                            if (selectedRowIndexes.length > 0) {
                              const updatedQuantities = inputQuantities.filter((_, index) => !selectedRowIndexes.includes(index));
                              setInputQuantities(updatedQuantities);

                            }
                          }
                          break;
                      }
                    }}
                    editSettings={{ allowEditing: true, allowAdding: false, allowDeleting: true, mode: 'Normal' }}
                    actionComplete={(args) => {
                      if (args.requestType === 'save') {
                        if (args.action === 'edit' && args.data) {
                          // Update the input quantities when editing is complete
                          const updatedData = args.data;
                          if (args.rowIndex !== undefined) {
                            const updatedQuantities = [...inputQuantities];
                            const rowIndex = args.rowIndex;
                            if (rowIndex >= 0 && rowIndex < updatedQuantities.length) {
                              // If material was changed, update the unit
                              if (updatedData.materialId && updatedData.materialId !== updatedQuantities[rowIndex].materialId) {
                                const material = materials.find(m => m.uuid === updatedData.materialId);
                                if (material) {
                                  updatedData.unit = material.baseUnitType || '';

                                }
                              }
                              
                              updatedQuantities[rowIndex] = {
                                ...updatedQuantities[rowIndex],
                                ...updatedData
                              };
                              setInputQuantities(updatedQuantities);
                            }
                          }
                        }
                      } else if (args.requestType === 'delete') {
                        // Handle deletion - update state to match grid
                        setInputQuantities(prev => {
                          const remainingQuantities = prev.filter((_, index) => index !== args.rowIndex);
                          return remainingQuantities;
                        });
                      }
                    }}
                    actionBegin={(args) => {
                      if (args.requestType === 'save') {
                        // Prevent saving if materialId is empty
                        if (args.data && !args.data.materialId) {
                          args.cancel = true;
                          // You could show a toast message here
                          return;
                        }
                      }
                    }}
                    dataBound={() => {
                      // Check if the dropdown column is properly configured
                      if (gridRef.current) {
                        // Ensure dropdown column is properly configured
                        gridRef.current.getColumns();
                      }
                    }}
                    dataSourceChanged={(args) => {
                      // Start editing the last row if it was just added
                      if (args.requestType === 'add' && gridRef.current) {
                        setTimeout(() => {
                          const lastIndex = inputQuantities.length - 1;
                          if (lastIndex >= 0) {
                            gridRef.current.selectRow(lastIndex);
                            gridRef.current.startEdit(lastIndex, 'materialId');
                          }
                        }, 100);
                      }
                    }}
                  >
                    <ColumnsDirective>
                      <ColumnDirective
                        field="rowId"
                        headerText=""
                        width={0}
                        isPrimaryKey={true}
                        visible={false}
                      />
                      <ColumnDirective
                        field="materialId"
                        headerText={t('routing.form.material')}
                        width={200}
                        editType="dropdownedit"
                        dataSource={materials}
                        foreignKeyValue="name"
                        foreignKeyField="uuid"
                        allowEditing={true}
                        validationRules={{ required: true }}
                        template={(props: any) => {
                          const material = findMaterialById(props.materialId);
                          return material?.name || (props.materialId ? `ID: ${props.materialId}` : '');
                        }}
                      />
                      <ColumnDirective
                        field="quantity"
                        headerText={t('routing.form.quantity')}
                        width={100}
                        textAlign="Right"
                        editType="numericedit"
                      />
                      <ColumnDirective
                        field="unit"
                        headerText={t('routing.form.unit')}
                        width={100}
                        editType="textedit"
                        allowEditing={false}
                        template={(props: any) => {
                          const material = findMaterialById(props.materialId);
                          const unitType = material?.baseUnitType || props.unit;
                          return translateUnitType(unitType);
                        }}
                      />
                    </ColumnsDirective>
                    <Inject services={[Page, Toolbar, Sort, Filter, Edit, CommandColumn, ForeignKey]} />
                  </GridComponent>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <ButtonComponent 
            onClick={onClose} 
            cssClass="e-outline"
            type="button"
          >
            {t('actions.cancel')}
          </ButtonComponent>
          <ButtonComponent 
            onClick={handleSave}
            isPrimary={true}
            type="button"
          >
            {t('actions.save')}
          </ButtonComponent>
        </div>
      </div>
    );
  }, [loading, inputQuantities, materials, gridRef, isOpen, t, setInputQuantities, onClose, onSave]);

  if (!isOpen) return null;
  
  return (
    <DialogComponent
      width="800px"
      isModal={true}
      visible={isOpen}
      header={t('routing.form.inputQuantities') + (operationName ? ` (${operationName})` : '')}
      showCloseIcon={true}
      closeOnEscape={false}
      allowDragging={true}
      target="#root"
      animationSettings={{ effect: 'None' }}
      beforeClose={(e) => {
        e.cancel = true;
        onClose();
      }}
    >
      {dialogContent()}
    </DialogComponent>
  );
};

export default InputQuantitiesDialog; 